import type { ProjectEnvelope, QuoteData } from '../../types/project-envelope';
import { deriveQuoteDisplayTotalsFromScope } from '../../utils/quote-display-totals.js';
import { getQuoteOutcome } from '../../services/quote-outcome';

export interface CrmProject extends Pick<Partial<ProjectEnvelope>, 'id' | 'title' | 'address' | 'quoteNumber' | 'qmQuoteNo' | 'status' | 'quoteStatus' | 'formData' | 'scope' | 'quoteData' | 'scopeGstInclusive' | 'companyId' | 'createdAt' | 'updatedAt' | 'created_at' | 'updated_at'> {
    workflowMode?: string | null;
    workflow_mode?: string | null;
    projectType?: string | null;
    project_type?: string | null;
    historySource?: string | null;
    history_source?: string | null;
    aiContext?: Record<string, unknown> | null;
    ai_context?: Record<string, unknown> | null;
    company_id?: string | null;
    quote_data?: QuoteData | null;
    total_inc_gst?: string | number | null;
    totalIncGST?: string | number | null;
    scope_gst_inclusive?: boolean | null;
}

export function getCrmCompanyId(project: CrmProject): string | null {
    return project.company_id || project.companyId || null;
}

/** Identity is explicit. Similar names are suggestions for review, never history links. */
export function indexCrmProjects<T extends CrmProject>(projects: T[]): Map<string, T[]> {
    const index = new Map<string, T[]>();
    for (const project of projects) {
        const companyId = getCrmCompanyId(project);
        if (!companyId) continue;
        const group = index.get(companyId) || [];
        group.push(project);
        index.set(companyId, group);
    }
    return index;
}

function money(value: unknown): number {
    const result = Number(String(value ?? '').replace(/[$,\s]/g, ''));
    return Number.isFinite(result) ? result : 0;
}

export interface CrmQuoteAmount {
    value: number;
    source: 'saved' | 'scope';
    label: 'Saved quote total incl GST' | 'Scope estimate incl GST';
}

/** Stage 5 snapshots persist their displayed total, including any visible HBCF scope item. */
export function getCrmQuoteAmount(project: CrmProject): CrmQuoteAmount | null {
    const quote = project.quoteData || project.quote_data;
    const saved = quote?.totals?.totalIncGst ?? quote?.totals?.totalIncGST ?? quote?.totalIncGst;
    if (saved != null && String(saved).trim() !== '' && Number.isFinite(Number(String(saved).replace(/[$,\s]/g, '')))) {
        return { value: money(saved), source: 'saved', label: 'Saved quote total incl GST' };
    }
    const scope = quote?.scope || project.scope || [];
    if (Array.isArray(scope) && scope.length > 0) {
        const inclusive = quote?.isGstInclusive ?? project.scopeGstInclusive ?? project.scope_gst_inclusive ?? true;
        const totals = deriveQuoteDisplayTotalsFromScope(scope, { isGstInclusive: inclusive });
        return { value: totals.totalIncGst, source: 'scope', label: 'Scope estimate incl GST' };
    }
    const total = project.totalIncGST ?? project.total_inc_gst ?? project.formData?.totalIncGST;
    if (total != null && String(total).trim() !== '' && Number.isFinite(Number(String(total).replace(/[$,\s]/g, '')))) {
        return { value: money(total), source: 'saved', label: 'Saved quote total incl GST' };
    }
    return null;
}

/** Numeric pipeline estimates retain a zero fallback; history exposes missing amounts explicitly. */
export function getCrmQuoteTotal(project: CrmProject): number {
    return getCrmQuoteAmount(project)?.value ?? 0;
}

export function isCrmHistoricalProject(project: CrmProject): boolean {
    const context = project.aiContext || project.ai_context;
    const historySource = project.historySource || project.history_source
        || (context && typeof context === 'object' ? (context as Record<string, unknown>).historySource : null);
    return (project.projectType || project.project_type) === 'history'
        || Boolean(historySource)
        || Boolean(project.formData?.historyImport)
        || ['history', 'history-import', 'historical-import'].includes(project.workflowMode || project.workflow_mode || '');
}

/** Historical imports remain searchable history; they are not current opportunities. */
export function isCrmActiveQuote(project: CrmProject): boolean {
    return !isCrmHistoricalProject(project)
        && !['archived', 'cancelled', 'canceled', 'deleted', 'completed', 'expired'].includes(String(project.status || '').toLowerCase())
        && ['draft', 'quoted', 'sent'].includes(getQuoteOutcome({ ...project }));
}

interface CrmContactIdentity {
    first_name?: string;
    firstName?: string;
    last_name?: string;
    lastName?: string;
    email?: string;
}

/** A retry or an existing-client choice must not add the same contact again. */
export function findExistingCrmContact<T extends CrmContactIdentity>(contacts: T[], draft: CrmContactIdentity): T | undefined {
    const normalise = (value?: string) => (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const fullName = (contact: CrmContactIdentity) => normalise([contact.first_name || contact.firstName, contact.last_name || contact.lastName].filter(Boolean).join(' '));
    const email = normalise(draft.email);
    const name = fullName(draft);
    return contacts.find(contact => {
        const existingEmail = normalise(contact.email);
        // Shared office mailboxes do not make two differently named people the same contact.
        return !!name && fullName(contact) === name && (!email || !existingEmail || existingEmail === email);
    });
}

/** The legacy page reads both aliases; backend contact routes prefer camelCase. */
export function applyCrmRecordPatch(record: Record<string, unknown>, patch: Record<string, unknown>): Record<string, unknown> {
    const result = { ...record, ...patch };
    for (const [key, value] of Object.entries(patch)) {
        const camel = key.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());
        result[camel] = value;
    }
    return result;
}

/**
 * The fields an edit form changed, compared against the same form as it was
 * opened. A company save sends only these (plus the id), so a cached row can
 * never write stale columns back over a newer server value.
 */
export function diffCrmRecordPatch(opened: Record<string, unknown>, edited: Record<string, unknown>): Record<string, unknown> {
    const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    const changes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(edited)) {
        if (!same(opened[key], value)) changes[key] = value;
    }
    return changes;
}

/** The PUT body for a notes autosave: the id and the notes, nothing else. */
export function companyNotesSaveBody(companyId: string, notes: string): { id: string; notes: string } {
    return { id: companyId, notes };
}

// The managing firm moves only through DataService.companies.setManager (or
// the audited Tidy up classify action), never through a profile save.
const MANAGER_BODY_KEYS = new Set(['managing_company_id', 'managingCompanyId']);

/**
 * The PUT body for an Edit-dialog save: the id plus the changed fields
 * (diffCrmRecordPatch), or null when the operator changed nothing.
 */
export function companyEditSaveBody(companyId: string, changes: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
    const body: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(changes || {})) {
        if (key !== 'id' && !MANAGER_BODY_KEYS.has(key)) body[key] = value;
    }
    return Object.keys(body).length ? { id: companyId, ...body } : null;
}

/*
 * Strata plan numbers. These three patterns mirror STRATA_PLAN_PATTERNS in
 * backend/src/services/crm-cleanup-proposals.js (parseStrataPlanNumber):
 * the LEFTMOST match in the name wins and leading zeros are dropped, so
 * "SP 019180" and "The Owners - Strata Plan 19180" are the same building.
 * Both parsers are pinned to src/components/crm/strata-plan-fixtures.json.
 */
const CRM_PLAN_PATTERNS: readonly RegExp[] = [
    /\bstrata\s*(?:plan|scheme)(?:\s*no\.?)?\s*[:#]?\s*(?:sp\s*)?(\d{2,7})\b/i,
    /\bs\.?\s?p\.?\s*[:#]?\s*(\d{2,7})\b/i,
    // A street number after "Owners Corporation -" is not a plan.
    /\bowners?\s+corp(?:oration)?\s*(?:no\.?)?\s*[:#]?\s*(\d{2,7})(?=\s*(?:$|[,;/()[\]-]))/i,
];
const stripPlanZeros = (digits: string) => digits.replace(/^0+(?=\d)/, '');

/** The plan digits in a company name ('' when none), leading zeros removed. */
export function crmPlanDigits(name?: string | null): string {
    const text = String(name || '');
    let best: RegExpMatchArray | null = null;
    for (const pattern of CRM_PLAN_PATTERNS) {
        const found = text.match(pattern);
        if (found && (!best || (found.index ?? 0) < (best.index ?? 0))) best = found;
    }
    return best?.[1] ? stripPlanZeros(best[1]) : '';
}

/**
 * The strata plan label for a company name, in the form the API returns as
 * plan_no ("SP19180"), or null when the name carries no plan.
 */
export function extractCrmPlanNumber(name?: string | null): string | null {
    const digits = crmPlanDigits(name);
    return digits ? `SP${digits}` : null;
}

/**
 * The plan digits of a job or a site ('' when none). A job's Strata Plan field
 * and properties.strata_plan hold "SP 19180", "Strata Plan 19180" or bare
 * digits. Mirrors propertyPlanNumber in crm-cleanup-proposals.js, the parser
 * the server uses when it counts a building's jobs (SPEC 2026-10-08 A1.4).
 */
export function crmSitePlanDigits(value?: string | null): string {
    const text = String(value ?? '').trim();
    const parsed = crmPlanDigits(text);
    if (parsed) return parsed;
    const digits = text.match(/^(?:sp\s*)?(\d{2,7})$/i)?.[1] || '';
    return digits ? stripPlanZeros(digits) : '';
}

const planText = (raw: unknown) => (typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '');

/**
 * The strata plan digits a job carries in its Strata Plan field ('' when none):
 * the first of strataplan, then the camelCase strataPlan some writers use, that
 * parses. An empty strataplan does not hide strataPlan.
 */
export function crmJobPlanDigits(formData?: Record<string, unknown> | null): string {
    for (const raw of [formData?.strataplan, formData?.strataPlan]) {
        const parsed = crmSitePlanDigits(planText(raw));
        if (parsed) return parsed;
    }
    return '';
}

/**
 * A job's strata plan digits ('' when none): its Strata Plan field, then its
 * Job's strata plan (quote_jobs.strata_plan, on a list row as
 * quoteJobStrataPlan). Mirrors jobPlanNumber in crm-cleanup-proposals.js, the
 * rule GET /companies/:id/managed and Tidy up use (SPEC 2026-10-08 A1.4).
 */
export function crmProjectPlanDigits(project: {
    formData?: unknown; form_data?: unknown; quoteJobStrataPlan?: unknown; quote_job_strata_plan?: unknown;
} | null | undefined): string {
    const form = project?.formData ?? project?.form_data;
    const fromForm = crmJobPlanDigits(form && typeof form === 'object' ? form as Record<string, unknown> : null);
    return fromForm || crmSitePlanDigits(planText(project?.quoteJobStrataPlan ?? project?.quote_job_strata_plan));
}

/**
 * A building's strata plan digits: the plan in its name, else the one plan its
 * sites carry ('' when they carry none or disagree). Mirrors buildingPlanNumber
 * in crm-cleanup-proposals.js (DEC-COUNCIL-018).
 */
export function crmBuildingPlanDigits(name: string | null | undefined, sitePlans: readonly unknown[] = []): string {
    const fromName = crmPlanDigits(name);
    if (fromName) return fromName;
    const plans = new Set(sitePlans.map(plan => crmSitePlanDigits(planText(plan))).filter(Boolean));
    return plans.size === 1 ? [...plans][0]! : '';
}

// Wording of an owners body, never a firm's name. Mirrors OWNERS_BODY_RE in
// backend/src/services/crm-cleanup-proposals.js.
const OWNERS_BODY_RE = /\b(?:body\s*corp(?:orate)?|owners?\s+corp(?:oration)?|the\s+owners|owners\s+of|strata\s+plan)\b/i;
const PLAN_ONLY_RE = /^(?:s\.?\s?p\.?|strata\s*(?:plan|scheme)(?:\s*no\.?)?)\s*[:#]?\s*\d{2,7}$/i;

/**
 * True when client text names the owners of a building, not a firm: "The
 * Owners of Strata Plan" (what Stage 5 prints for a strata job), "The Owners -
 * Strata Plan 2068", "Body Corporate", "Owners Corporation", or nothing but a
 * plan ("SP 2068"). A firm such as "Strata Partners" or "Waratah Strata
 * Management" is not (SPEC 2026-10-08 C4, D5).
 */
export function isOwnersWording(value?: string | null): boolean {
    const text = String(value ?? '').trim();
    if (!text) return false;
    return OWNERS_BODY_RE.test(text) || PLAN_ONLY_RE.test(text);
}

/** A search typed as a plan number ("19180", "SP 19180", "sp019180") as plan digits, else ''. */
export function crmPlanQueryDigits(query?: string | null): string {
    const found = String(query || '').trim().match(/^(?:s\.?\s?p\.?|strata\s*(?:plan|scheme)(?:\s*no\.?)?)?\s*[:#]?\s*(\d{1,7})$/i);
    return found?.[1] ? stripPlanZeros(found[1]) : '';
}

interface CrmCompanyIdentity {
    name?: string | null;
    company_type?: string | null;
    companyType?: string | null;
    managing_company_id?: string | null;
    managingCompanyId?: string | null;
}

/**
 * An owners corporation (strata plan) is the legal addressee for one building,
 * not a client. It is either typed body_corporate already, or its name carries
 * a strata plan number (most legacy rows are still typed strata_manager).
 */
export function isCrmOwnersCorporation(company: CrmCompanyIdentity | null | undefined): boolean {
    if (!company) return false;
    return (company.company_type || company.companyType) === 'body_corporate' || !!extractCrmPlanNumber(company.name);
}

/**
 * The linked client's name on a job row ('' when there is none or it is a
 * building). The project list joins the linked company's `company_name`, and
 * `company_type` when the server sends it. A building row is never a job's
 * client (SPEC 2026-10-08 D1): it is typed body_corporate, its name carries a
 * strata plan ("Harbour Views SP 2068"), or its name is owners wording ("The
 * Owners - SP 2068"). A job still linked to a building, until Tidy up moves it
 * to the firm, then reads its firm from the request instead.
 */
export function crmLinkedFirmName(project: {
    companyName?: unknown; company_name?: unknown; companyType?: unknown; company_type?: unknown;
} | null | undefined): string {
    const raw = project?.companyName ?? project?.company_name;
    const name = typeof raw === 'string' ? raw.trim() : '';
    if (!name || isOwnersWording(name)) return '';
    const type = project?.companyType ?? project?.company_type;
    return isCrmOwnersCorporation({ name, company_type: typeof type === 'string' ? type : null }) ? '' : name;
}

/** The firm that manages an owners corporation (migration 119), in either key case. */
export function getCrmManagingCompanyId(company: CrmCompanyIdentity | null | undefined): string | null {
    return company?.managingCompanyId || company?.managing_company_id || null;
}
